import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { renderIcon } from '../utils/iconRenderer';
import { FiPlus, FiTrash2, FiEdit2, FiImage, FiVideo } from 'react-icons/fi';
import { LookBookMedia } from '../types/models';

const LookBook: React.FC = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState<LookBookMedia[]>([]);
  const [draggedItem, setDraggedItem] = useState<LookBookMedia | null>(null);

  // Load media from localStorage
  useEffect(() => {
    const savedMedia = localStorage.getItem('lookbookMedia');
    if (savedMedia) {
      setMedia(JSON.parse(savedMedia));
    } else {
      // Dummy data
      setMedia([
        {
          id: '1',
          title: 'Summer Collection 2025',
          description: 'Beautiful summer designs',
          mediaType: 'photo',
          url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&h=500&fit=crop',
          order: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '2',
          title: 'Winter Vibes',
          description: 'Cozy winter collection',
          mediaType: 'photo',
          url: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=500&fit=crop',
          order: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          id: '3',
          title: 'Product Showcase',
          description: 'Latest products video',
          mediaType: 'video',
          url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          order: 2,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ]);
    }
  }, []);

  // Save media to localStorage
  useEffect(() => {
    localStorage.setItem('lookbookMedia', JSON.stringify(media));
  }, [media]);

  const handleDragStart = (item: LookBookMedia) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetItem: LookBookMedia) => {
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const draggedIndex = media.findIndex(m => m.id === draggedItem.id);
    const targetIndex = media.findIndex(m => m.id === targetItem.id);

    const newMedia = [...media];
    [newMedia[draggedIndex], newMedia[targetIndex]] = [newMedia[targetIndex], newMedia[draggedIndex]];

    // Update order numbers
    newMedia.forEach((item, index) => {
      item.order = index;
    });

    setMedia(newMedia);
    setDraggedItem(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Kya aap is media ko delete karna chahte ho?')) {
      const newMedia = media.filter(m => m.id !== id);
      // Update order numbers
      newMedia.forEach((item, index) => {
        item.order = index;
      });
      setMedia(newMedia);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/lookbook/edit/${id}`);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">LookBook</h2>
            <p className="text-gray-600 mt-1">Manage your photos and videos here</p>
          </div>
          <button
            onClick={() => navigate('/lookbook/add')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {renderIcon(FiPlus, { size: 20 })}
            <span>Add Media</span>
          </button>
        </div>

        {/* Media Grid */}
        {media.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {renderIcon(FiImage, { size: 32, className: 'text-gray-400' })}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No media available</h3>
            <p className="text-gray-600 mb-6">Upload your first photo or video</p>
            <button
              onClick={() => navigate('/lookbook/add')}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {renderIcon(FiPlus, { size: 18 })}
              <span>Add Media</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {media.map((item) => (
              <div
                key={item.id}
                draggable
                onDragStart={() => handleDragStart(item)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(item)}
                className={`bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all cursor-move ${
                  draggedItem?.id === item.id ? 'opacity-50' : ''
                }`}
              >
                {/* Media Preview */}
                <div className="relative w-full h-48 bg-slate-100 overflow-hidden group">
                  {item.mediaType === 'photo' ? (
                    <img
                      src={item.url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-200">
                      <div className="text-center">
                        {renderIcon(FiVideo, { size: 48, className: 'text-gray-400 mx-auto mb-2' })}
                        <p className="text-sm text-gray-600">Video</p>
                      </div>
                    </div>
                  )}

                  {/* Media Type Badge */}
                  <div className="absolute top-2 right-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                      item.mediaType === 'photo' ? 'bg-blue-500' : 'bg-purple-500'
                    }`}>
                      {item.mediaType === 'photo' ? 'Photo' : 'Video'}
                    </span>
                  </div>

                  {/* Order Badge */}
                  <div className="absolute top-2 left-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-white">
                      #{item.order + 1}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                  {item.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(item.createdAt).toLocaleDateString('hi-IN')}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 p-4 border-t border-slate-100">
                  <button
                    onClick={() => handleEdit(item.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    {renderIcon(FiEdit2, { size: 16 })}
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    {renderIcon(FiTrash2, { size: 16 })}
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        {media.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Easily reorder your photos and videos using drag and drop
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LookBook;
